import { X } from 'lucide-react'
import type { EmployeeDto, AlertAnchorStep, ScheduleEventDto } from '../../../../lib/api/v2/planning'
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../../../ui/sheet'
import { ProjectDetail } from './ProjectDetail'

interface ProjectDetailDrawerProps {
  /** When null the drawer has nothing to show; `open` should also be false. */
  projectId: string | null
  open: boolean
  /**
   * Docked = the drawer is a real column beside a LIVE agenda (Elodie 2026-06-14: planifier sans
   * fermer/rouvrir). PlanningPage turns this on only on desktop (≥1024px) when a project is open,
   * and hides the À planifier/À suivre aside to make room. When false → the modal overlay below
   * (small screens, or no project open).
   */
  docked?: boolean
  onClose: () => void
  onMutated: () => void
  /** Fired when the detail has loaded, with the project's planned pose date (yyyy-MM-dd) or null. */
  onLoaded?: (poseStartIso: string | null) => void
  userNameById?: Record<string, string>
  employees?: EmployeeDto[]
  anchorStep?: AlertAnchorStep
  /**
   * Show one of the project's créneaux in the agenda (SOLY-PLAN-003 MS-12).
   *
   * Docked, the agenda is the column next door: it jumps to the week and the drawer stays open.
   * Modal, the drawer COVERS the agenda — so it closes first, otherwise the gesture moves a grid
   * nobody can see. One rule, two renderings; the caller does not have to know which mode is on.
   */
  onFocusSlot?: (slot: ScheduleEventDto) => void
}

/**
 * Project detail beside the agenda. Two presentations from one body:
 *  · DOCKED (desktop, project open) — a 640px column; the agenda stays fully interactive next to it
 *    so a pose/métré can be (re)planned without ever closing the drawer (Elodie field feedback).
 *  · MODAL (small screens) — the original right-hand Sheet sliding over a dimmed agenda (MS-22).
 * Close via ← Retour, ×, or Escape. Hosts the shared <ProjectDetail> in both modes.
 */
export function ProjectDetailDrawer({
  projectId, open, docked, onClose, onMutated, onLoaded, userNameById, employees, anchorStep, onFocusSlot,
}: ProjectDetailDrawerProps) {
  const focusSlot = onFocusSlot && ((slot: ScheduleEventDto) => {
    if (!docked) onClose()
    onFocusSlot(slot)
  })
  // Docked: a plain grid column (no portal, no overlay) → the agenda beside it keeps every click.
  if (docked) {
    if (!open || projectId === null) return null
    return (
      <div
        className="flex h-full min-h-0 flex-col overflow-hidden border-l bg-background"
        data-testid="planning-detail-drawer"
        data-docked="true"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="planning-detail-back"
            className="text-xs text-muted-foreground hover:underline"
          >
            ← Retour
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer le détail"
            data-testid="planning-detail-close"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <ProjectDetail
            projectId={projectId}
            onMutated={onMutated}
            onLoaded={onLoaded}
            userNameById={userNameById}
            employees={employees}
            anchorStep={anchorStep}
            onFocusSlot={focusSlot}
          />
        </div>
      </div>
    )
  }

  return (
    <Sheet open={open && projectId !== null} onOpenChange={(next) => { if (!next) onClose() }}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-[640px] gap-0 p-0 sm:max-w-[640px]"
        data-testid="planning-detail-drawer"
        // Radix focuses the first tabbable on open; the anchor scroll handles section focus instead.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetTitle className="sr-only">Détail du chantier</SheetTitle>
        <SheetDescription className="sr-only">Fiche opérationnelle du chantier sélectionné</SheetDescription>
        <div className="border-b px-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="planning-detail-back"
            className="text-xs text-muted-foreground hover:underline"
          >
            ← Retour
          </button>
        </div>
        {projectId && (
          // min-h-0 + flex-1 so <ProjectDetail h-full> resolves to the space left under the ← Retour bar
          // (not the whole sheet) — otherwise the bottom (history expanded) is cut off and won't scroll.
          <div className="min-h-0 flex-1 overflow-hidden">
            <ProjectDetail
              projectId={projectId}
              onMutated={onMutated}
              onLoaded={onLoaded}
              userNameById={userNameById}
              employees={employees}
              anchorStep={anchorStep}
              onFocusSlot={focusSlot}
            />
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
